import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Col,
  Grid,
  Icon,
  Input,
  InputContainer,
  Title,
  UploadPicture,
} from '@common/components';
import { useUpdateMutation } from '@dashboard/api/users';
import { getBaseUrlRefresh, getApiUrl } from '@app/api';
import { useTogglePasswordVisibility } from '@auth/hooks/useTooglePasswordVisibility';
import { useAuth } from '@auth/hooks';
import { useAppDispatch } from '@app/hooks';
import { useInputErrors } from '@app/hooks/useInputErrors';
import { useNavigate } from 'react-router-dom';

import {
  isEmail,
  isEmpty,
  isPasswordValid,
  passwordsAreEquals,
} from '@app/util/validations';
import { setCurrentUser } from '@auth/slices/auth';
import { AlertModal } from '@app/components';

import profileClasses from './UserProfileView.module.scss';

export default function UserProfileView() {
  const { user } = useAuth();
  const { t } = useTranslation('dashboard\\UserProfileView');
  const { errors, resetErrors, setErrors, updateErrors } = useInputErrors();
  const navigate = useNavigate();

  const [email, setEmail] = useState(user ? user.email : '');
  const [firstName, setFirstName] = useState(user ? user.first_name : '');
  const [lastName, setLastName] = useState(user ? user.last_name : '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState(false);

  const [inputFilePicture, setInputFilePicture] = useState(
    user && user?.settings?.avatar
      ? `${getBaseUrlRefresh(user?.settings?.avatar)}`
      : undefined,
  );

  const [updateUser, { isLoading }] = useUpdateMutation();
  const dispatch = useAppDispatch();

  const { passwordVisibility, passwordIcon, handlePasswordVisibility } =
    useTogglePasswordVisibility();

  const {
    passwordVisibility: passwordVisibilityConfirm,
    passwordIcon: passwordIconConfirm,
    handlePasswordVisibility: handlePasswordVisibilityConfirm,
  } = useTogglePasswordVisibility();

  const onReset = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    resetErrors();
    navigate('/');
    setTouched(false);
  };

  const onValidate = () => {
    let isValid = resetErrors();
    setErrors({});

    if (isEmpty(firstName)) {
      isValid = updateErrors({
        firstName: t('nameValidationRequired'),
      });
    }
    if (isEmpty(lastName)) {
      isValid = updateErrors({
        lastName: t('surnameValidationRequired'),
      });
    }
    if (isEmpty(email)) {
      isValid = updateErrors({
        email: t('emailValidationRequired'),
      });
    } else if (!isEmail(email)) {
      isValid = updateErrors({
        email: t('emailValidationFormat'),
      });
    }
    if (!isEmpty(password) || !isEmpty(confirmPassword)) {
      if (!isPasswordValid(password)) {
        isValid = updateErrors({
          password: t('passwordValidationFormat'),
        });
      }

      if (isEmpty(confirmPassword)) {
        isValid = updateErrors({
          confirmPassword: t('passwordValidationRequired'),
        });
      } else if (!passwordsAreEquals(password, confirmPassword)) {
        isValid = updateErrors({
          confirmPassword: t('passwordValidationEqual'),
        });
      }
    }

    return isValid;
  };
  const [showModal, setShowModal] = useState(false);

  const onSubmit = async (event) => {
    setTouched(true);
    event.preventDefault();
    if (onValidate()) {
      if (password) {
        const profilePassword = {
          id: user.uuid,
          password,
          password_confirmation: confirmPassword,
        };

        await updateUser(profilePassword);
      }
      let profile = {
        id: user.uuid,
        email,
        first_name: firstName,
        last_name: lastName,
      };

      if (!inputFilePicture.startsWith(getApiUrl('/'))) {
        profile = {
          ...profile,
          settings: { avatar: inputFilePicture },
        };
      }

      try {
        const { data } = await updateUser(profile);
        if (data.data) {
          setShowModal(true);
        }

        setTimeout(() => {
          dispatch(setCurrentUser({ ...user, ...data.data }));
        }, 300);
      } catch (error) {
        setShowModal(false);
      }
    }
  };

  useEffect(() => {}, [user]);

  useEffect(() => {
    if (touched) {
      onValidate();
    }
  }, [email, password, confirmPassword, firstName, lastName]);

  return (
    <div className={profileClasses.userProfile}>
      <Title color="primary" className={profileClasses.title}>
        {t('titleLabel')}
      </Title>
      {user && (
        <form style={{ maxWidth: '550px' }}>
          <div className={profileClasses.avatarContainer}>
            <UploadPicture
              className={profileClasses.avatar}
              inputFilePicture={inputFilePicture}
              setInputFilePicture={setInputFilePicture}
            />
          </div>
          <Grid align="top" justify="center">
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputNameLabel')}
                name="firstName"
                error={errors.firstName}
              >
                <Input
                  name="firstName"
                  fullWidth
                  type="text"
                  placeholder={t('inputNameLabel')}
                  onChange={setFirstName}
                  value={firstName}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputSurnameLabel')}
                name="lastName"
                error={errors.lastName}
              >
                <Input
                  name="lastName"
                  fullWidth
                  type="text"
                  placeholder={t('inputSurnameLabel')}
                  onChange={setLastName}
                  value={lastName}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6}>
              <InputContainer
                className={profileClasses.input}
                classNameContainer={profileClasses.inputContainer}
                placeholder={t('inputEmailLabel')}
                name="email"
                error={errors.email}
              >
                <Input
                  name="email"
                  fullWidth
                  type="email"
                  placeholder={t('inputEmailLabel')}
                  onChange={setEmail}
                  value={email}
                />
              </InputContainer>
            </Col>
            <Col xss={12} sm={6} />
          </Grid>
          <Grid align="top" mode="column">
            <Col xss={12} sm={12}>
              <Title
                type="h2"
                color="primary"
                className={profileClasses.titleMin}
              >
                {t('titleChangePassword')}
              </Title>
              <p className={profileClasses.subtitle}>
                {t('hintChangePassword')}
              </p>
            </Col>
            <Grid align="top" justify="center">
              <Col xss={12} sm={6}>
                <InputContainer
                  className={profileClasses.input}
                  classNameContainer={profileClasses.inputContainer}
                  suffix={
                    <Button minimal onClick={handlePasswordVisibility}>
                      <Icon icon={passwordIcon} />
                    </Button>
                  }
                  placeholder={t('inputPasswordLabel')}
                  name="password"
                  error={errors.password}
                >
                  <Input
                    name="password"
                    fullWidth
                    type={passwordVisibility ? 'text' : 'password'}
                    placeholder={t('inputPasswordLabel')}
                    onChange={setPassword}
                    value={password}
                    autoComplete="new-password"
                  />
                </InputContainer>
              </Col>
              <Col xss={12} sm={6}>
                <InputContainer
                  className={profileClasses.input}
                  classNameContainer={profileClasses.inputContainer}
                  suffix={
                    <Button minimal onClick={handlePasswordVisibilityConfirm}>
                      <Icon icon={passwordIconConfirm} />
                    </Button>
                  }
                  placeholder={t('inputPasswordRepeatLabel')}
                  name="confirmPassword"
                  error={errors.confirmPassword}
                >
                  <Input
                    name="confirmPassword"
                    fullWidth
                    type={passwordVisibilityConfirm ? 'text' : 'password'}
                    placeholder={t('inputPasswordRepeatLabel')}
                    onChange={setConfirmPassword}
                    value={confirmPassword}
                    autoComplete="new-password"
                  />
                </InputContainer>
              </Col>
            </Grid>
          </Grid>
          <Grid
            className={profileClasses.buttonContainer}
            align="middle"
            justify="center"
          >
            <Col xss={12} sm={6}>
              <Button
                className={profileClasses.button}
                variant="flat"
                color="error"
                onClick={onReset}
              >
                {t('btnCancel')}
              </Button>
            </Col>
            <Col xss={12} sm={6}>
              <Button
                className={profileClasses.button}
                variant="flat"
                color="accent"
                onClick={onSubmit}
                disabled={isLoading}
              >
                {t('btnSave')}
              </Button>
            </Col>
          </Grid>

          <AlertModal
            show={showModal}
            type="done"
            title={t('modalTitle')}
            text={t('modalText')}
            onClose={() => setShowModal(false)}
          />
        </form>
      )}
    </div>
  );
}
